/* RubricsAuditBadge — Milestone 3 Report Center accessory.
   Renders a small, accessible pill with the BCP compliance status.
   States: compliant (green) · recommendations (amber) · corrections_required (red) · legacy (gray).
   The badge is display-only when badge.engine !== "episcopal". */

import { useState, useRef, useEffect } from "react";
import { CheckCircle2, AlertTriangle, AlertOctagon, HelpCircle } from "lucide-react";

const PALETTE = {
  compliant: { bg: "#e6f4ea", border: "#8bc79b", fg: "#1f6a34",
               Icon: CheckCircle2, label: "BCP compliant" },
  recommendations: { bg: "#fef4d6", border: "#e8c968", fg: "#7a5a05",
               Icon: AlertTriangle, label: "Recommendations" },
  corrections_required: { bg: "#fde5e0", border: "#e08a7a", fg: "#8b2812",
               Icon: AlertOctagon, label: "Corrections required" },
  legacy: { bg: "#eee9dc", border: "#c9c2ac", fg: "#6a6551",
               Icon: HelpCircle, label: "Audit unavailable" },
};

function describe(badge) {
  const status = badge?.status || "legacy";
  const cfg = PALETTE[status] || PALETTE.legacy;
  const score = typeof badge?.score === "number" ? `${badge.score}%` : null;
  const label = badge?.label || cfg.label;
  const counts = badge?.counts || {};
  const blocking = counts.blocking || 0;
  const warning = counts.warning || 0;
  const recommendation = counts.recommendation || 0;

  let text;
  if (status === "compliant") text = score ? `BCP Compliance: ${score}` : "BCP compliant";
  else if (status === "recommendations") {
    const bits = [];
    if (score) bits.push(`BCP Compliance: ${score}`);
    if (warning) bits.push(`${warning} recommendation${warning === 1 ? "" : "s"}`);
    text = bits.join(" · ") || label;
  } else if (status === "corrections_required") {
    text = `Corrections required · ${blocking} blocking issue${blocking === 1 ? "" : "s"}`;
  } else {
    text = "Audit unavailable (legacy report)";
  }
  return { cfg, text, blocking, warning, recommendation };
}

export default function RubricsAuditBadge({ badge, onOpenAudit }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const popRef = useRef(null);

  // Close on outside click / Esc
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Only render for Episcopal engine audits.
  if (!badge || (badge.engine && badge.engine !== "episcopal" && badge.status !== "legacy")) {
    return null;
  }
  // If badge is missing entirely, render nothing (caller may pass null).
  if (!badge) return null;

  const { cfg, text } = describe(badge);
  const Icon = cfg.Icon;
  const top = Array.isArray(badge.top_findings) ? badge.top_findings : [];

  const toggle = () => setOpen((v) => !v);
  const onKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      toggle();
    }
  };

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <button
        ref={btnRef}
        type="button"
        role="status"
        aria-label={`BCP rubrics audit: ${text}`}
        aria-expanded={open}
        aria-haspopup="dialog"
        data-testid="rubrics-audit-badge"
        onClick={(e) => { e.stopPropagation(); toggle(); }}
        onKeyDown={onKeyDown}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "3px 9px",
          borderRadius: 999,
          background: cfg.bg,
          border: `1px solid ${cfg.border}`,
          color: cfg.fg,
          font: "600 11.5px/1.2 -apple-system, Segoe UI, system-ui, sans-serif",
          letterSpacing: 0.2,
          cursor: "pointer",
        }}
      >
        <Icon size={12} aria-hidden="true" />
        <span>{text}</span>
      </button>
      {open && (
        <div
          ref={popRef}
          role="dialog"
          aria-label="Top rubric findings"
          data-testid="rubrics-audit-popover"
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            zIndex: 60,
            width: 320,
            padding: 12,
            background: "#fff",
            border: "1px solid #d9cfae",
            borderRadius: 8,
            boxShadow: "0 8px 24px rgba(31,42,68,0.14)",
            fontSize: 12.5,
            color: "#2a2a2a",
          }}
        >
          <div style={{ fontWeight: 700, color: "#1F2A44", marginBottom: 6 }}>
            {badge.label || cfg.label}
          </div>
          {top.length === 0 && (
            <div style={{ color: "#6a6551" }}>
              No blocking or warning findings.
            </div>
          )}
          {top.length > 0 && (
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {top.map((f, i) => (
                <li key={i} style={{ marginBottom: 6 }}>
                  <b style={{ color: cfg.fg }}>{(f.severity || "").toUpperCase()}</b>
                  {" · "}{f.message || f.code}
                  {f.citation?.reference && (
                    <div style={{ color: "#6a6551", fontSize: 11.5, marginTop: 2 }}>
                      {f.citation.reference}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
          {typeof onOpenAudit === "function" && (
            <button
              type="button"
              data-testid="rubrics-audit-open-full"
              onClick={(e) => { e.stopPropagation(); onOpenAudit(); setOpen(false); }}
              style={{
                marginTop: 10, background: "#1F2A44", color: "#fff",
                border: "none", borderRadius: 6, padding: "5px 10px",
                fontSize: 11.5, fontWeight: 600, cursor: "pointer",
              }}
            >
              View full audit inside the report
            </button>
          )}
        </div>
      )}
    </div>
  );
}
